import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Drawer, Input, Layout, Segmented, Spin } from 'antd';
import { ArrowLeftOutlined, CloseOutlined, ContainerTwoTone, DownloadOutlined, PaperClipOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import NavigationBar from '../../components/NavigationBar';
import DOTheme from '../../components/directOrders/doTheme';
import useElementWidth from '../../components/dynamicForms/useElementWidth';
import { fmtWhen } from '../../components/dynamicForms/ResponsesView';
import { ARCHIVED_FORMS, HIDDEN_COLUMNS, archivedForm } from '../../components/clientAdmin/archivedForms';
import { API_ORIGIN } from '../../config';
import { openApiFile } from '../../utils/apiAuth';
import '../../styles/clientAdmin.css';
import '../../styles/dynamicForms.css';

const PAGE = 50;

const refused = (status) => Object.assign(new Error('Old form responses are available to staff who can manage client forms.'), { forbidden: true, status });

const isEmpty = (v) => v === null || v === undefined || v === '';
const humanize = (column) => column.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

const show = (value, kind) => {
  if (isEmpty(value)) return null;
  if (kind === 'flag') return Number(value) === 1 || value === true ? 'Yes' : 'No';
  if (kind === 'date') return dayjs(value).isValid() ? dayjs(value).format('D MMMM YYYY') : String(value);
  if (kind === 'money' && /^\d+(\.\d+)?$/.test(String(value))) return `€${Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  return String(value);
};

/** Every row of an archived form. DFM2026's list is paged, so it is read page by page. */
export const loadArchivedRows = async (form) => {
  if (!form.list.paged) {
    const response = await fetch(`${API_ORIGIN}${form.list.url}`);
    if (response.status === 403) throw refused(403);
    const data = await response.json();
    if (!response.ok || data.success === false) throw new Error(data.error || data.message || 'Could not load the responses');
    return data[form.list.key] || [];
  }
  const rows = [];
  for (let page = 1; page <= 50; page += 1) {
    const response = await fetch(`${API_ORIGIN}${form.list.url}?limit=1000&page=${page}`);
    if (response.status === 403) throw refused(403);
    const data = await response.json();
    if (!response.ok || data.success === false) throw new Error(data.error || data.message || 'Could not load the responses');
    rows.push(...(data[form.list.key] || []));
    if (!data.pagination || page >= data.pagination.pages) break;
  }
  return rows;
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  let text = String(value);
  // keep spreadsheet formulas from running when the file is opened in Excel
  if (/^\s*([=+@]|-[^\s])/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadText = (content, filename) => {
  const url = URL.createObjectURL(new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** The columns not described in the form's config, so no answer is ever hidden. */
const otherColumns = (form, row) => {
  const known = new Set([form.when, ...HIDDEN_COLUMNS, ...form.sections.flatMap((s) => s.fields.map(([c]) => c)), ...form.files.map((f) => f.column)]);
  return Object.keys(row).filter((c) => !known.has(c) && !isEmpty(row[c]));
};

/**
 * Read-only responses of a retired hard-coded form (RockNMalta, Dance On, Għannejja Ġodda,
 * DFM2026), from the backend archive. Search, open a response, download its files, export CSV.
 */
const ArchivedFormResponses = () => {
  const { formKey } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const form = archivedForm(formKey);
  const rootRef = useRef(null);
  const width = useElementWidth(rootRef);

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [order, setOrder] = useState('newest');
  const [shown, setShown] = useState(PAGE);
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!form) return;
    try {
      setLoading(true);
      setError(null);
      setRows(await loadArchivedRows(form));
    } catch (err) {
      setError(err.forbidden ? 'forbidden' : err.message || 'Could not load the responses');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    setRows(null);
    setOpenId(null);
    setQuery('');
    setShown(PAGE);
    load();
  }, [load]);

  const term = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!form || !rows) return [];
    const list = term
      ? rows.filter((r) => Object.values(r).some((v) => !isEmpty(v) && String(v).toLowerCase().includes(term)))
      : rows;
    const sign = order === 'newest' ? -1 : 1;
    return [...list].sort((a, b) => sign * (new Date(a[form.when]) - new Date(b[form.when])) || sign * (a.id - b.id));
  }, [rows, term, order, form]);

  useEffect(() => { setShown(PAGE); }, [term, order]);

  if (!form) return <Navigate to="/client-admin" replace />;

  const open = rows?.find((r) => r.id === openId) || null;
  const latest = rows?.reduce((max, r) => (!max || new Date(r[form.when]) > new Date(max) ? r[form.when] : max), null);
  const split = width >= 980;

  const openFile = async (file, row) => {
    try {
      await openApiFile(`${API_ORIGIN}${file.url(row)}`, { download: true, filename: file.name(row) });
    } catch (err) {
      message.error(err.message || 'Could not download the file');
    }
  };

  const exportCsv = async () => {
    const day = dayjs().format('YYYY-MM-DD');
    try {
      setExporting(true);
      if (form.exportUrl) {
        // RockNMalta has its own export on the backend, with the labels its team is used to
        const response = await fetch(`${API_ORIGIN}${form.exportUrl}`);
        if (!response.ok) throw new Error('Could not export the responses');
        downloadText((await response.text()).replace(/^﻿/, ''), `${form.key}-responses-${day}.csv`);
      } else {
        const fields = form.sections.flatMap((s) => s.fields);
        const header = ['ID', 'Received', ...fields.map(([, label]) => label), ...form.files.map((f) => f.label)];
        const lines = [header.map(csvCell).join(',')];
        filtered.forEach((r) => {
          lines.push([
            r.id,
            r[form.when] ? dayjs(r[form.when]).format('DD/MM/YYYY HH:mm') : '',
            ...fields.map(([c, , kind]) => show(r[c], kind) ?? ''),
            ...form.files.map((f) => (f.url(r) ? f.name(r) : '')),
          ].map(csvCell).join(','));
        });
        downloadText(lines.join('\r\n'), `${form.key}-responses-${day}.csv`);
      }
      message.success('CSV downloaded');
    } catch (err) {
      message.error(err.message || 'Could not export the responses');
    } finally {
      setExporting(false);
    }
  };

  const detail = open && (
    <article className="dfw-detail" aria-labelledby="arc-detail-title">
      <header className="dfw-detail__head">
        <div>
          <h3 className="dfw-detail__title" id="arc-detail-title">{form.title(open)}</h3>
          <p className="dfw-detail__meta">
            #{open.id}{open[form.when] ? ` · ${dayjs(open[form.when]).format('D MMMM YYYY [at] HH:mm')}` : ''}
          </p>
        </div>
        {split && <Button type="text" icon={<CloseOutlined aria-hidden="true" />} onClick={() => setOpenId(null)} aria-label="Close response" />}
      </header>
      {form.sections.filter((s) => !s.when || s.when(open)).map((s) => (
        <section key={s.title} className="arc-section" aria-label={s.title}>
          <h4 className="arc-section__title">{s.title}</h4>
          <dl className="dfw-answers">
            {s.fields.map(([column, label, kind]) => {
              const value = show(open[column], kind);
              return (
                <div className="dfw-answers__row" key={column}>
                  <dt>{label}</dt>
                  <dd>{value === null ? <span className="dfw-answer__none">No answer</span> : <span className="dfw-answer__text">{value}</span>}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
      {form.files.length > 0 && (
        <section className="arc-section" aria-label="Files">
          <h4 className="arc-section__title">Files</h4>
          <dl className="dfw-answers">
            {form.files.map((f) => (
              <div className="dfw-answers__row" key={f.column}>
                <dt>{f.label}</dt>
                <dd>
                  {f.url(open) ? (
                    <span className="dfw-file">
                      <PaperClipOutlined aria-hidden="true" />
                      <span className="dfw-file__name">{f.name(open)}</span>
                      <Button size="small" icon={<DownloadOutlined aria-hidden="true" />} onClick={() => openFile(f, open)}>Download</Button>
                    </span>
                  ) : (
                    <span className="dfw-answer__none">Not uploaded</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      {otherColumns(form, open).length > 0 && (
        <section className="arc-section" aria-label="Other details">
          <h4 className="arc-section__title">Other details</h4>
          <dl className="dfw-answers">
            {otherColumns(form, open).map((c) => (
              <div className="dfw-answers__row" key={c}>
                <dt>{humanize(c)}</dt>
                <dd><span className="dfw-answer__text">{String(open[c])}</span></dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </article>
  );

  let body;
  if (error === 'forbidden') {
    body = (
      <div className="dfw-empty">
        <strong>You can’t open these responses</strong>
        <span>Old form responses are available to staff who can manage client forms.</span>
      </div>
    );
  } else if (error) {
    body = <Alert type="error" showIcon message="Could not load the responses" description={error} action={<Button size="small" onClick={load}>Try again</Button>} />;
  } else if (!rows) {
    body = <div className="dfw-empty"><Spin /></div>;
  } else {
    body = (
      <>
        <div className="dfw-builder__bar">
          <p className="dfw-builder__lede">
            {rows.length === 0 ? 'No responses.' : `${rows.length.toLocaleString()} ${rows.length === 1 ? 'response' : 'responses'}`}
            {latest && <> · the last on {dayjs(latest).format('D MMMM YYYY')}</>}
          </p>
          <span className="dfw-grow" />
          <Button icon={<ReloadOutlined aria-hidden="true" />} onClick={load} loading={loading}>Refresh</Button>
          <Button icon={<DownloadOutlined aria-hidden="true" />} onClick={exportCsv} loading={exporting} disabled={rows.length === 0}>
            Export CSV
          </Button>
        </div>
        <div className="dfw-filters">
          <Input
            className="dfw-search"
            allowClear
            prefix={<SearchOutlined aria-hidden="true" />}
            placeholder="Search names, emails or any answer"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search responses"
          />
          <span className="dfw-grow" />
          <Segmented size="small" value={order} onChange={setOrder} options={[{ value: 'newest', label: 'Newest' }, { value: 'oldest', label: 'Oldest' }]} aria-label="Order" />
        </div>
        <div className={`dfw-responses__body${split && open ? ' is-split' : ''}`}>
          <div className="dfw-responses__list">
            {filtered.length === 0 ? (
              <div className="dfw-empty">
                <strong>{rows.length === 0 ? 'No responses' : 'No responses match'}</strong>
                <span>{rows.length === 0 ? 'This form has no saved responses.' : 'Try another search.'}</span>
              </div>
            ) : (
              <>
                <div className="dfw-rtable arc-table" role="table" aria-label="Responses" style={{ '--dfw-cols': `64px minmax(0, 1.6fr) ${form.columns.map(() => 'minmax(0, 1fr)').join(' ')} 132px` }}>
                  <div className="dfw-rtable__head" role="row">
                    <span role="columnheader">#</span>
                    <span role="columnheader">Applicant</span>
                    {form.columns.map((c) => <span role="columnheader" key={c.label}>{c.label}</span>)}
                    <span role="columnheader">Received</span>
                  </div>
                  {filtered.slice(0, shown).map((r) => {
                    const files = form.files.some((f) => f.url(r));
                    return (
                      <button
                        type="button"
                        role="row"
                        key={r.id}
                        className={`dfw-rtable__row${r.id === openId ? ' is-on' : ''}`}
                        onClick={() => setOpenId(r.id === openId ? null : r.id)}
                        aria-expanded={r.id === openId}
                      >
                        <span role="cell" className="dfw-num dfw-rtable__id">{r.id}</span>
                        <span role="cell" className="dfw-rtable__who">
                          <strong>{form.title(r)}{files && <PaperClipOutlined className="dfw-rtable__clip" aria-label="has files" />}</strong>
                          {form.subtitle(r) && <small>{form.subtitle(r)}</small>}
                        </span>
                        {form.columns.map((c) => (
                          <span role="cell" className="dfw-rtable__pick" key={c.label}>
                            {isEmpty(c.value(r)) ? <span className="dfw-answer__none">None</span> : c.value(r)}
                          </span>
                        ))}
                        <span role="cell" className="dfw-rtable__when">{r[form.when] ? fmtWhen(r[form.when]) : ''}</span>
                      </button>
                    );
                  })}
                </div>
                {filtered.length > shown && (
                  <Button className="dfw-more" type="link" onClick={() => setShown((n) => n + PAGE)}>
                    Show {Math.min(PAGE, filtered.length - shown)} more of {filtered.length - shown}
                  </Button>
                )}
                {term && <p className="dfw-count">{filtered.length} of {rows.length} responses</p>}
              </>
            )}
          </div>
          {split && open && <div className="dfw-responses__detail">{detail}</div>}
        </div>
        {!split && (
          <Drawer
            open={!!open}
            onClose={() => setOpenId(null)}
            width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 24 : 560)}
            title={open ? `#${open.id}` : ''}
            rootClassName="do-scope dfw-drawer"
          >
            {detail}
          </Drawer>
        )}
      </>
    );
  }

  return (
    <DOTheme>
      <Layout className="do-shell do-scope ca-page">
        <NavigationBar />
        <main className="do-main ca-main">
          <aside className="ca-rail">
            <div>
              <Link to="/client-admin" className="arc-back"><ArrowLeftOutlined aria-hidden="true" /> Client admin</Link>
              <h1 className="ca-rail__name">Older forms</h1>
            </div>
            <nav className="dfw-rail__nav" aria-label="Older forms">
              {ARCHIVED_FORMS.map((f) => (
                <button
                  type="button"
                  key={f.key}
                  className={`dfw-rail__item${f.key === form.key ? ' is-on' : ''}`}
                  aria-current={f.key === form.key ? 'page' : undefined}
                  onClick={() => navigate(`/client-admin/archive/${f.key}`)}
                >
                  <span className="dfw-rail__icon" aria-hidden="true"><ContainerTwoTone twoToneColor="#2748d9" /></span>
                  <span className="dfw-rail__text">{f.name}</span>
                </button>
              ))}
            </nav>
            <p className="arc-note">
              These forms are closed. Their responses are kept so you can look them up and export them; they can’t be changed or deleted here.
            </p>
          </aside>
          <div className="ca-sheet" ref={rootRef}>
            <header className="dfw-head">
              <h2 className="dfw-title">{form.name}</h2>
              <p className="dfw-sub">{form.what}. Read only.</p>
            </header>
            {body}
          </div>
        </main>
      </Layout>
    </DOTheme>
  );
};

export default ArchivedFormResponses;
